import {
  DecisionTreeNode,
  MacroDecisionTreeNodeCollectInput,
  MacroDecisionTreeNodeData,
  MacroDecisionTreeNodeType,
} from '@frontapp/workflows/decision_trees';
import {brandExpressionId} from '@frontapp/workflows/expressions';
import {type MacroInputDefinition, type MacroInputId, makeMacroInputId} from '@frontapp/workflows/macros';
import type {Node} from 'reactflow';
import uuid from 'uuid-random';

import {I18n} from '../../libs/shared/core/src/helpers/internationalization/i18n';
import {defineMessage} from '../../libs/shared/core/src/helpers/internationalization/message';
import type {ClientMacroDecisionTreeNodeData} from '../../libs/shared/core/src/helpers/types/macroFlowBuilderTypes';
import {isDefined} from '../../libs/shared/core/src/helpers/typescript/typeHelpers';
import type {TreeData} from '../../libs/shared/ui/src/components/decisionTrees/decisionTreeBuilder/types';
import {MacroModel} from '../models/api/macroModel';
import {buildExpression} from '../settings/editors/expressions/expressionInferHelpers';
import {Expression} from '../settings/editors/expressions/expressionModel';
import {ExpressionsContext} from '../settings/editors/expressions/expressionsContext';
import {computeMaxId} from '../settings/editors/expressions/expressionState';
import {getMacroInputIdFromAtom} from '../settings/editors/expressions/infer/getMacroInputValue';
import {buildAtom} from '../settings/editors/expressions/infer/helpers';
import {TransformerIdsEnum} from '../settings/editors/expressions/infer/transformers';

export const macrosHelpArticleUrl = 'https://help.front.com/en/articles/824576';

export function makeMacroInputExpressionName(macroInput: MacroInputDefinition, i18n: I18n) {
  return i18n._(defineMessage({id: 'macro-input-expression-name', message: `${macroInput.name} answer`}));
}

export function getMacroInputsFromClientTreeData(
  treeData: TreeData<ClientMacroDecisionTreeNodeData>,
): ReadonlyArray<MacroInputDefinition> | undefined {
  const collectInputNode = treeData.nodes.find(isClientCollectInputNode);
  return collectInputNode?.data.inputs;
}

function isClientCollectInputNode(
  node: Node<ClientMacroDecisionTreeNodeData>,
): node is Node<MacroDecisionTreeNodeCollectInput> {
  return node.data.type === MacroDecisionTreeNodeType.COLLECT_INPUT;
}

export function getMacroInputs(macro: MacroModel): ReadonlyArray<MacroInputDefinition> | undefined {
  const collectInputNode = macro.decisionTree?.tree_data?.nodes.find(isCollectInputNode);
  return collectInputNode?.data.inputs;
}

function isCollectInputNode(
  node: DecisionTreeNode<MacroDecisionTreeNodeData>,
): node is DecisionTreeNode<MacroDecisionTreeNodeCollectInput> {
  return node.data.type === MacroDecisionTreeNodeType.COLLECT_INPUT;
}

export function getMacroInputIdFromExpression(expression: Expression): MacroInputId | undefined {
  if (expression.atoms.length !== 1) {
    return undefined;
  }

  const [atom] = expression.atoms;

  return getMacroInputIdFromAtom(atom);
}

export function generateMacroInputId(): MacroInputId {
  return makeMacroInputId(`macro-input:${uuid()}`);
}

export function makeExpressionsFromMacroInputIds(
  macroInputIds: ReadonlyArray<MacroInputId>,
  macroInputs: ReadonlyArray<MacroInputDefinition>,
  context: ExpressionsContext,
  i18n: I18n,
  areAutoCreated: boolean,
): ReadonlyArray<Expression> {
  const maxId = computeMaxId(context.expressions);

  return macroInputIds
    .map((macroInputIdToAdd, index) => {
      const macroInput = macroInputs.find((input) => input.id === macroInputIdToAdd);
      if (!macroInput) {
        return undefined;
      }
      return buildExpressionFromInput(macroInput, context, i18n, maxId + index + 1, areAutoCreated);
    })
    .filter(isDefined);
}

function buildExpressionFromInput(
  macroInput: MacroInputDefinition,
  context: ExpressionsContext,
  i18n: I18n,
  idIndex: number,
  isAutoCreated: boolean,
) {
  const getInputValueAtom = buildAtom(
    {transform: TransformerIdsEnum.GET_INPUT_VALUE, params: {input_id: macroInput.id}},
    context.initialInfer,
    context,
  );

  return buildExpression(
    brandExpressionId(`local:${idIndex}`),
    makeMacroInputExpressionName(macroInput, i18n),
    [getInputValueAtom],
    context,
    isAutoCreated,
  );
}
